import React from 'react';
import { Body1, Body5, Container, SVG_LOADING_ICON_TYPES, SvgLoadingIcon } from '@ui';
import { FlatButton } from '@ui/buttons';
import { CloseButton } from '@ui/buttons/CloseButton';
import { SpeakerDocumentSelection } from '../SpeakerDocumentSelection';
import { DocumentSelection } from '../DocumentSelection';
import { Modal } from '@ui';
import { AI_TOOLS, DOCUMENT_SELECTION_MODES } from '@constants';
import { CompanyTagDocumentSelection } from '../CompanyTagDocumentSelection';
import { CsvUserDocumentSelection } from '../CsvUserDocumentSelection';
import { useCurrentUserToolsAvailable } from '@hooks/user/useCurrentUser';
import { useColorTheme } from '@hooks';
import { TrackingEvent } from '@classes/TrackingEvent';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';

const useTrackView = () => {
  React.useEffect(() => {
    TrackingEvent.create('View Document Selection Modal', {});
  }, []);

  return null;
};

const useTrackMode = (docSelectMode) => {
  React.useEffect(() => {
    TrackingEvent.create('Document Selection Mode', {
      Mode: docSelectMode,
    });
  }, [docSelectMode]);

  return null;
};

export const DocumentSelectionModal = ({
  onDismiss,
  docSelectModes,
  docs,
  isLoadingDocs,
  tickers,
  selectedDocs,
  handleAddDocumentSpeakerSearch,
  handleAddDocument,
  handleRemoveDocument,
}) => {
  const colorTheme = useColorTheme();
  const toolsAvailable = useCurrentUserToolsAvailable();
  const isCompanyTaggingEnabled = toolsAvailable.includes(AI_TOOLS.COMPANY_TAGGING);
  const [modalProps] = React.useState({ maximizeWidth: true, forceHeight: true });

  const [docSelectMode, setDocSelectMode] = React.useState(docSelectModes[0]);
  const isModeEnabled = (mode) => docSelectModes.includes(mode);
  const isCsvMode = docSelectMode === DOCUMENT_SELECTION_MODES.userCsv;
  const titleText = isCsvMode ? 'Select CSV' : 'Select Document';

  useTrackView();
  useTrackMode(docSelectModes);

  if (isLoadingDocs && docSelectMode === DOCUMENT_SELECTION_MODES.docType) {
    return (
      <Modal onDismiss={onDismiss} modalProps={modalProps}>
        <Container>
          <Container row verticallyCenter spaceBetween fullWidth>
            <Container>
              <Body1 bold>Select Document</Body1>
            </Container>
            <Container bottom={1} left={16}>
              <CloseButton onClick={onDismiss} />
            </Container>
          </Container>
          <Container row verticallyCenter top={48} bottom={99}>
            <SvgLoadingIcon type={SVG_LOADING_ICON_TYPES.bars} color={colorTheme.text} />
            <Container left={16}>
              <Body5>Loading...</Body5>
            </Container>
          </Container>
        </Container>
      </Modal>
    );
  }

  return (
    <Modal onDismiss={onDismiss} modalProps={modalProps}>
      <Container row verticallyCenter spaceBetween>
        <Container row verticallyCenter spaceBetween fullWidth>
          <Container>
            <Body1 bold>{titleText}</Body1>
          </Container>
          <Container row style={{ gap: '8px' }}>
            {docSelectMode !== DOCUMENT_SELECTION_MODES.docType && isModeEnabled(DOCUMENT_SELECTION_MODES.docType) && (
              <FlatButton onClick={() => setDocSelectMode(DOCUMENT_SELECTION_MODES.docType)}>
                Search by document type
              </FlatButton>
            )}
            {docSelectMode !== DOCUMENT_SELECTION_MODES.speaker && isModeEnabled(DOCUMENT_SELECTION_MODES.speaker) && (
              <FlatButton onClick={() => setDocSelectMode(DOCUMENT_SELECTION_MODES.speaker)}>Search by Speaker</FlatButton>
            )}
            {docSelectMode !== DOCUMENT_SELECTION_MODES.companyTag &&
              isModeEnabled(DOCUMENT_SELECTION_MODES.companyTag) &&
              isCompanyTaggingEnabled && (
                <FlatButton onClick={() => setDocSelectMode(DOCUMENT_SELECTION_MODES.companyTag)}>
                  Search by Company / Tag
                </FlatButton>
              )}
          </Container>
        </Container>
        <Container top={0} left={16}>
          <Container row verticallyCenter>
            <SkeletonButton onClick={onDismiss}>Save</SkeletonButton>
            <Container left={8}>
              <CloseButton onClick={onDismiss} />
            </Container>
          </Container>
        </Container>
      </Container>
      <Container top={24}>
        {docSelectMode === DOCUMENT_SELECTION_MODES.speaker && (
          <SpeakerDocumentSelection
            onSelect={handleAddDocumentSpeakerSearch}
            onRemoveSelect={handleRemoveDocument}
            selectedDocs={selectedDocs}
          />
        )}
        {docSelectMode === DOCUMENT_SELECTION_MODES.docType && (
          <DocumentSelection
            tickers={tickers}
            docs={docs}
            onSelect={handleAddDocument}
            onRemoveSelect={handleRemoveDocument}
            selectedDocs={selectedDocs}
            onClose={onDismiss}
          />
        )}
        {docSelectMode === DOCUMENT_SELECTION_MODES.companyTag && (
          <CompanyTagDocumentSelection
            selectedDocs={selectedDocs}
            onSelect={handleAddDocument}
            onRemoveSelect={handleRemoveDocument}
          />
        )}
        {docSelectMode === DOCUMENT_SELECTION_MODES.userCsv && (
          <CsvUserDocumentSelection
            selectedDocs={selectedDocs}
            onSelect={handleAddDocument}
            onRemoveSelect={handleRemoveDocument}
          />
        )}
      </Container>
    </Modal>
  );
};
